import gsap from 'gsap';

/**
 * Tween helpers that collapse to their END STATE while a table is snapping.
 *
 * Call sites keep one code path: they describe the animation they would play,
 * and pass whether this table is currently snapping (see utils/tableSnapState).
 * When it is, the element is placed at the tween's final values with gsap.set —
 * no frames scheduled, no GSAP ticker work — and the tween's onComplete is
 * invoked synchronously so any state the animation was responsible for settling
 * (a "cards collected" flag, a visibility toggle) still lands.
 */

/** Vars that describe timing/lifecycle rather than the element's end state. */
const TIMING_KEYS = [
  'duration',
  'delay',
  'ease',
  'repeat',
  'repeatDelay',
  'repeatRefresh',
  'yoyo',
  'stagger',
  'onStart',
  'onUpdate',
  'onRepeat',
  'onInterrupt',
  'onComplete',
  'callbackScope'
];

/**
 * Strip the timing/callback vars from a tween description, leaving only the
 * properties that define where the element ends up. Exported for unit testing.
 */
export const finalVars = (vars: gsap.TweenVars): gsap.TweenVars => {
  const out: gsap.TweenVars = {};
  Object.keys(vars || {}).forEach((key) => {
    if (TIMING_KEYS.indexOf(key) === -1) out[key] = (vars as any)[key];
  });
  return out;
};

/** gsap.to, or an instant jump to its end state when `snap` is true. */
export const snapOrTo = (
  snap: boolean,
  target: gsap.TweenTarget,
  vars: gsap.TweenVars
): gsap.core.Tween | null => {
  if (!snap) return gsap.to(target, vars);
  gsap.set(target, finalVars(vars));
  if (typeof vars.onComplete === 'function') vars.onComplete();
  return null;
};

/** gsap.fromTo, or an instant jump to its end state when `snap` is true. */
export const snapOrFromTo = (
  snap: boolean,
  target: gsap.TweenTarget,
  fromVars: gsap.TweenVars,
  toVars: gsap.TweenVars
): gsap.core.Tween | null => {
  if (!snap) return gsap.fromTo(target, fromVars, toVars);
  gsap.set(target, finalVars(toVars));
  if (typeof toVars.onComplete === 'function') toVars.onComplete();
  return null;
};
